import { RdProjectDeletionErrorSchema } from '@common/neo.tax';
import { Modal, Stack, useModal } from '@ui-components/neo.tax';
import { ReactElement } from 'react';

import { useRdContext } from '@neo.tax/rd/context';
import { useDeleteRdProject, RdProject } from '@neo.tax/rd/hooks';
import {
  getExtendedErrorsOrDefault,
  getPath,
  Path,
  useCurrentCompanyId,
  useMounted,
  useNavigateTo,
  DeleteConfirmationLayout,
} from '@neo.tax/shared';

import { ButtonStyled } from './delete-rd-project.styled';

type DeleteRdProjectProps = {
  rdProject: RdProject;
  analyticIds: {
    deleteButtonId: string;
    deleteConfirmationButtonId: string;
  };
};

export const DeleteRdProject = ({ analyticIds, rdProject }: DeleteRdProjectProps): ReactElement => {
  const navigate = useNavigateTo();
  const companyId = useCurrentCompanyId();
  const { fiscalYearId } = useRdContext();

  const mounted = useMounted();
  const { isOpen: isDeleteProjectModalOpen, setIsOpen: _setIsOpen } = useModal();

  // prevent memory leaks by checking if the component is still mounted
  const setIsOpen = (v: boolean): void => {
    if (mounted.current) _setIsOpen(v);
  };

  const [deleteRdProject, { loading }] = useDeleteRdProject({
    rdProjectId: rdProject.id,
    onCompleted: () => {
      setIsOpen(false);
      navigate(getPath({ path: Path.RD_PROJECTS, companyId, fiscalYearId }));
    },
  });

  const handleOpenModal = (): void => {
    setIsOpen(true);
  };

  const handleCloseModal = (): void => {
    setIsOpen(false);
  };

  const handleConfirmDelete = async (): Promise<void> => {
    try {
      await deleteRdProject();
      // Only navigate after successful deletion
      navigate(getPath({ path: Path.RD_PROJECTS, companyId, fiscalYearId }));
    } catch (error) {
      const extendedErrors = getExtendedErrorsOrDefault(
        error,
        RdProjectDeletionErrorSchema,
        'Failed to delete project. Please try again.',
      );
      throw new Error(
        `This project cannot be deleted for the following reasons: ${extendedErrors
          .map((error) => error.description)
          .join(', ')}.`,
      );
    }
  };

  return (
    <Stack direction="column">
      <ButtonStyled
        id={analyticIds.deleteButtonId}
        size="xs"
        variant="action"
        rounded="regular"
        onClick={handleOpenModal}
      >
        Delete Project
      </ButtonStyled>
      <Modal isOpen={isDeleteProjectModalOpen} setIsOpen={handleCloseModal}>
        <DeleteConfirmationLayout
          title="Delete Project"
          description="Would you like to delete this project? This action cannot be undone."
          close={handleCloseModal}
          deleteMutation={handleConfirmDelete}
          loading={loading}
          confirmButtonId={analyticIds.deleteConfirmationButtonId}
        />
      </Modal>
    </Stack>
  );
};
